import React, { useState, useEffect } from 'react';
import { 
  User, 
  Mail, 
  ArrowLeft, 
  Edit2, 
  Save, 
  X 
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { updateProfile, sendPasswordResetEmail } from 'firebase/auth';
import { doc, updateDoc, Timestamp } from 'firebase/firestore';
import { db, auth } from '../lib/firebase';
import { useAuth } from '../contexts/AuthContext';
import { 
  getUserProfile, 
  getRoleDisplayName,
  formatDateTime 
} from '../lib/userService';
import Header from './Header';

const UserProfile = () => {
  const { currentUser } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editingName, setEditingName] = useState(false);
  const [newDisplayName, setNewDisplayName] = useState('');

  useEffect(() => {
    if (currentUser) {
      fetchUserProfile();
    }
  }, [currentUser]);

  const fetchUserProfile = async () => {
    try {
      setLoading(true);
      setError('');
      
      const userProfile = await getUserProfile(currentUser.uid);
      setProfile(userProfile);
      setNewDisplayName(userProfile.displayName || '');
    } catch (error) {
      console.error('Error fetching user profile:', error);
      setError('Error al cargar el perfil del usuario');
    } finally {
      setLoading(false);
    }
  };


  const updateDisplayName = async () => {
    if (!newDisplayName.trim()) {
      setError('El nombre no puede estar vacío');
      return;
    }

    try {
      setUpdating(true);
      setError('');
      setSuccess('');

      // Update Firebase Auth profile
      await updateProfile(currentUser, {
        displayName: newDisplayName.trim()
      });

      // Update Firestore user document
      const userRef = doc(db, 'users', currentUser.uid);
      await updateDoc(userRef, {
        displayName: newDisplayName.trim(),
        updatedAt: Timestamp.now()
      });

      setSuccess('Nombre actualizado exitosamente');
      setEditingName(false);
      
      // Refresh profile
      await fetchUserProfile();
    } catch (error) {
      console.error('Error updating display name:', error);
      setError('Error al actualizar el nombre');
    } finally {
      setUpdating(false);
    }
  };

  const requestPasswordReset = async () => {
    if (!window.confirm('¿Estás seguro de que deseas restablecer tu contraseña? Se enviará un correo electrónico con instrucciones.')) {
      return;
    }

    try {
      setUpdating(true);
      setError('');
      setSuccess('');

      await sendPasswordResetEmail(auth, currentUser.email);
      setSuccess('Correo de restablecimiento de contraseña enviado exitosamente');
    } catch (error) {
      console.error('Error sending password reset email:', error);
      setError('Error al enviar el correo de restablecimiento de contraseña');
    } finally {
      setUpdating(false);
    }
  };


  const cancelEditName = () => {
    setEditingName(false);
    setNewDisplayName(profile?.displayName || '');
    setError('');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <Header />
        <div className="max-w-4xl mx-auto p-6">
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">
            Cargando perfil del usuario...
          </div>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <Header />
        <div className="max-w-4xl mx-auto p-6">
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 px-4 py-3 rounded-md">
            No se pudo cargar el perfil del usuario
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <Header />
      <div className="max-w-4xl mx-auto p-6">
        <div className="mb-6">
          <Link 
            to="/"
            className="inline-flex items-center space-x-2 text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
          >
            <ArrowLeft className="h-4 w-4" />
            <span>Volver al Dashboard</span>
          </Link>
        </div>
        
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm">
          <div className="p-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center space-x-2">
              <User className="h-6 w-6 text-blue-600 dark:text-blue-400" />
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Mi Perfil</h2>
            </div>
          </div>

          {/* Error and Success Messages */}
          {error && (
            <div className="mx-6 mt-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 px-4 py-3 rounded-md">
              {error}
            </div>
          )}
          
          {success && (
            <div className="mx-6 mt-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-green-600 dark:text-green-400 px-4 py-3 rounded-md">
              {success}
            </div>
          )}

          {/* Profile Information */}
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Basic Information */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">Información Personal</h3>
                
                {/* Display Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Nombre para mostrar
                  </label>
                  {editingName ? (
                    <div className="flex items-center space-x-2">
                      <input
                        type="text"
                        value={newDisplayName}
                        onChange={(e) => setNewDisplayName(e.target.value)}
                        className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
                        placeholder="Ingresa tu nombre"
                        disabled={updating}
                      />
                      <button
                        onClick={updateDisplayName}
                        disabled={updating}
                        className="px-3 py-2 bg-green-600 dark:bg-green-500 text-white rounded-md hover:bg-green-700 dark:hover:bg-green-600 focus:outline-none focus:ring-2 focus:ring-green-500 dark:focus:ring-green-400 disabled:opacity-50"
                      >
                        <Save className="h-4 w-4" />
                      </button>
                      <button
                        onClick={cancelEditName}
                        disabled={updating}
                        className="px-3 py-2 bg-gray-600 dark:bg-gray-500 text-white rounded-md hover:bg-gray-700 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500 dark:focus:ring-gray-400 disabled:opacity-50"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center space-x-2">
                      <p className="text-gray-900 dark:text-white">
                        {profile.displayName || 'Sin nombre configurado'}
                      </p>
                      <button
                        onClick={() => setEditingName(true)}
                        className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
                      >
                        <Edit2 className="h-4 w-4" />
                      </button>
                    </div>
                  )}
                </div>

                {/* Email */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Correo electrónico
                  </label>
                  <p className="text-gray-900 dark:text-white">{profile.email}</p>
                </div>

                {/* Role */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Rol
                  </label>
                  <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${
                    profile.role === 'admin' ? 'bg-red-100 text-red-800' :
                    profile.role === 'shopkeeper' ? 'bg-blue-100 text-blue-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {getRoleDisplayName(profile.role)}
                  </span>
                </div>
              </div>

              {/* Account Information */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">Información de Cuenta</h3>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Fecha de registro
                  </label>
                  <p className="text-gray-900 dark:text-white">{formatDateTime(profile.createdAt)}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Último acceso
                  </label>
                  <p className="text-gray-900 dark:text-white">
                    {profile.lastLogin ? formatDateTime(profile.lastLogin) : 'Nunca'}
                  </p>
                </div>

              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="p-6 border-t border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Acciones</h3>
            
            <div className="space-y-3">
              <button
                onClick={requestPasswordReset}
                disabled={updating}
                className="inline-flex items-center space-x-2 px-4 py-2 bg-blue-600 dark:bg-blue-500 text-white rounded-md hover:bg-blue-700 dark:hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 disabled:opacity-50"
              >
                <Mail className="h-4 w-4" />
                <span>Restablecer contraseña</span>
              </button>

            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default UserProfile;